import request from './request'

/** 素材库列表（后端 M4 开放；M3 可先通过任务详情获取素材） */
export function fetchMaterials(params = {}) {
  return request.get('/materials', { params })
}

/** 单图导出 PSD（单图层 + 可选透明底 PNG），产物自动登记为素材 */
export function exportPsd(id) {
  return request.post('/materials/exportPsd', null, { params: { id } })
}

/** 把生成结果加入素材库 */
export function addToLib(id) {
  return request.post('/materials/addToLib', null, { params: { id } })
}

/** 按条件打包下载素材（返回 zip blob；传 jobId 或 source/media/type/keyword） */
export function downloadMaterialsZip(params = {}) {
  return request.get('/materials/zip', { params, responseType: 'blob', timeout: 300000 })
}

/** 「改一改」重新生成的预计消耗积分（与后端实际扣费同一套算法，含模型分级单价） */
export function fetchRedoPoints(id) {
  return request.get('/materials/redoPoints', { params: { id } })
}

/** 批量删除素材（单个删除就是只传一个 id）；后端会同时清掉磁盘文件（若无其它记录引用） */
export function deleteMaterials(ids) {
  return request.post('/materials/delete', null, { params: { ids: (ids || []).join(',') } })
}

/**
 * 影片畫質提升（超分）：先查價（可用檔位、預計積分、餘額）。
 * ★必須走 JSON body —— 後端 MaterialController.upscaleOptions 是讀 getRawData() 解析的。
 */
export function upscaleOptions(path) {
  return request.post('/materials/upscaleOptions', { path })
}

/** 影片畫質提升：執行（成功才扣積分；產物會替換原影片並刪掉原檔，所以讀超時給長一点） */
export function upscaleVideo(path, target) {
  return request.post('/materials/upscaleVideo', { path, target }, { timeout: 900000 })
}

/**
 * 影片合成：多段拼接 + 轉場 + 配樂 + 畫中畫（**純本地 ffmpeg，不收費**）。
 *
 * @param payload {clips:[], transition, transition_sec, music, music_volume,
 *                 keep_original_audio, overlay, overlay_scale, overlay_pos}
 * @returns {path, seconds, width, height, clips}
 * ★超時給 15 分鐘：多段重編碼很吃 CPU，長片可能幾分鐘
 */
export function composeVideo(payload) {
  return request.post('/materials/composeVideo', payload, { timeout: 900000 })
}

/**
 * 影片首帧缩略图（卡片预览用）+ 宽高/时长。
 * 后端用 ffmpeg 抽一帧存 jpg（带缓存）—— 不要让浏览器自己取首帧：
 * /files/view 不支持 Range，浏览器会把整个文件拉完。
 *
 * @returns {thumb, width, height, seconds}
 */
export function videoThumb(path, width) {
  return request.post('/materials/videoThumb', { path, width: width || 320 })
}

/** AI 字幕：查價（時長 / 每秒積分 / 預計積分 / 餘額） */
export function subtitleOptions(path) {
  return request.post('/materials/subtitleOptions', { path })
}

/**
 * AI 字幕：識別（火山 MediaKit 語音轉字幕）→ {srt, srt_path, subtitles[], count, seconds, points}
 * ★超時給 10 分鐘：ASR 是異步任務，長片要排隊
 */
export function asrSubtitles(path, speakerInfo) {
  return request.post('/materials/asrSubtitles',
    { path, speaker_info: !!speakerInfo }, { timeout: 900000 })
}

/** AI 去字幕：查價（字幕擦除·精細化版） */
export function eraseOptions(path) {
  return request.post('/materials/eraseOptions', { path })
}

/**
 * AI 去字幕：執行（火山 MediaKit 字幕擦除·精細化版）→ {path, seconds, width, height, points}
 * ★超時給 30 分鐘：擦除要逐幀重建畫面，比畫質提升慢不少
 */
export function eraseSubtitle(path, modelVersion, regions) {
  return request.post('/materials/eraseSubtitle',
    { path, model_version: modelVersion || 'v5', regions: regions || [] },
    { timeout: 1800000 })
}

/**
 * AI 字幕：把 SRT 燒進畫面（純本地 ffmpeg，不消耗積分）。
 * @param opts {position: bottom|top|center, fontSize: small|medium|large,
 *              fontColor: 'FFFFFF', outlineColor: '000000'}
 */
export function burnSubtitle(path, srt, opts) {
  const o = opts || {}
  return request.post('/materials/burnSubtitle', {
    path,
    srt,
    position: o.position || 'bottom',
    font_size: o.fontSize || 'medium',
    font_color: o.fontColor || 'FFFFFF',
    outline_color: o.outlineColor || '000000'
  }, { timeout: 1800000 })
}

/** AI 語音（文字轉語音）查價：按字数 + 最低收費 */
export function voiceQuote(text) {
  return request.post('/materials/voiceQuote', { text })
}

/** AI 語音：可选音色清单（后端 VoiceKit 内置一份中文常用音色；无效 ID 上游会静默回退默认音色，所以只允许从这里选） */
export function voiceOptions() {
  return request.get('/materials/voiceOptions')
}

/**
 * AI 語音：音色試聽（**免費，不扣積分**）
 *
 * 后端全平台共用一句固定話術，第一次點時真生成一次並落盤快取
 * （data/outputs/voice/preview/<speaker>.mp3），之後任何人再點都直接播同一份文件，
 * 不再調上游、不再產生費用。返回 {path, cached}。
 */
export function voicePreview(speaker) {
  return request.get('/materials/voicePreview', {
    params: { speaker: speaker || '' },
    // 第一次要真生成（正常 1~3 秒）；给 60 秒上限，超时就报错，
    // 不能让用户盯着「生成中」干等（后端中途成功也没关系，下次点是缓存）
    timeout: 60000
  })
}

/** AI 語音：文字轉語音（火山 MediaKit → 豆包音频生成）→ {path, seconds, chars, points, materialId} */
export function ttsVoice(text, opts) {
  const o = opts || {}
  return request.post('/materials/tts', {
    text,
    speaker: o.speaker || '',
    tts_model: o.tts_model || '',
    speed: o.speed || 0,
    pitch: o.pitch || 0,
    loudness: o.loudness || 0
  }, { timeout: 600000 })
}

/** 影片自動配音查價：按视频时长 */
export function dubOptions(path) {
  return request.post('/materials/dubOptions', { path })
}

/**
 * 影片自動配音：識別 → 翻譯 → 配音 → 本地換音軌（不做口型对齐）
 * ★超时给 40 分钟：多段异步任务串起来可能十几分钟
 */
export function dubVideo(path, targetLang, translate, opts) {
  const o = opts || {}
  return request.post('/materials/dubVideo', {
    path,
    target_lang: targetLang || 'zh-TW',
    translate: translate !== false,
    speaker: o.speaker || ''
  }, { timeout: 2400000 })
}

/**
 * 把配音（AI 語音生成的 mp3）合到影片上 —— **只换音轨**，不重编码画面。
 *
 * 返回里的 mode：
 *   video_loop = 配音比原片长 → 画面循环补足到配音长度（配音不会被截）
 *   audio_pad  = 配音比原片短 → 保留完整画面，结尾补静音
 */
export function muxVoice(video, audio) {
  return request.post('/materials/muxVoice', { video, audio }, { timeout: 600000 })
}

/**
 * 參考影片「線上剪輯」：按時間區間截一段（只做分割，產物落在源檔同目錄）。
 *
 * @param path 源影片路徑（data/outputs/ 下）
 * @param segments 要保留的區間列表 [[start, end], ...]（按時間順序）；剪輯器裡刪掉的段不傳進來。
 *                 只留一段時後端直接切，不做拼接。
 * @returns {path, seconds} seconds 是後端實測時長（超限判定與計費都依賴它）
 */
export function trimVideo(path, segments) {
  return request.post('/materials/trimVideo', { path, segments })
}

/**
 * 貼上影片連結 → 拉成參考影片。
 *
 * 支援 YouTube / TikTok / Facebook / Instagram / X / Vimeo / Dailymotion / Bilibili
 * （以及 yt-dlp 內建 extractor 覆蓋的其它站點）。
 * 後端是「yt-dlp 優先、真實瀏覽器兜底」，可能要幾十秒 ~ 幾分鐘，所以超時放寬。
 * 失敗時 message 是「一句話說明原因」（需登入 / 有地區限制 / 連結認不出），可直接顯示給使用者。
 */
export function fetchVideoByUrl(url, outDir) {
  return request.post('/materials/fetchVideo',
    { url, out_dir: outDir || '' },
    { timeout: 600000 })
}
